<template>
  <section>
    <div class="container cont checkout-step checkout-step--clean">
      <form class="checkout-step__form checkout-step__form--wide" method="post" @submit.prevent="continueSale()">
        <div class="checkout-step__intro">
          <small class="form-text text-muted checkout-step__eyebrow">Paso 2 de 4</small>
          <h2 class="text-justify va-h6 checkout-step__title"><strong>Datos del comprador</strong></h2>
          <small class="form-text text-muted checkout-step__hint">Usa un solo correo y define al responsable del retiro antes del pago.</small>
        </div>

        <div class="checkout-form-grid">
          <div class="checkout-field checkout-field--full" @keyup="getCustomer()">
            <label class="checkout-label" for="pickup-email">Correo de compra</label>
            <input id="pickup-email" class="form-control checkout-input" type="email" v-model="email" name="email" placeholder="tu@email.com" required="">
          </div>

          <template v-if="require_account">
            <div class="checkout-field">
              <label class="checkout-label" for="pickup-phone">Teléfono de contacto</label>
              <input id="pickup-phone" class="form-control checkout-input" type="text" v-model="phone" name="phone" placeholder="0999999999" required="">
            </div>

            <div class="checkout-field">
              <label class="checkout-label" for="pickup-address">Dirección de facturación</label>
              <input id="pickup-address" class="form-control checkout-input" type="text" v-model="address" name="address" placeholder="Dirección de domicilio" required="">
            </div>

            <div class="checkout-field checkout-field--full">
              <label class="checkout-label" for="pickup-password">Contraseña de acceso</label>
              <input id="pickup-password" class="form-control checkout-input" type="password" v-model="password" name="password" placeholder="Crea una contraseña para revisar tus tickets rápido" required="">
            </div>
          </template>
        </div>

        <div v-if="require_account" class="checkout-check">
          <div
            class="checkout-check__button"
            :class="{ 'is-checked': acceptedTerms }"
            role="checkbox"
            tabindex="0"
            :aria-checked="acceptedTerms ? 'true' : 'false'"
            @click="toggleAcceptedTerms"
            @keydown.enter.prevent="toggleAcceptedTerms"
            @keydown.space.prevent="toggleAcceptedTerms"
          >
            <span class="checkout-check__box" aria-hidden="true"></span>
            <span class="checkout-check__text">Al continuar aceptas los <span class="checkout-check__link" role="button" tabindex="0" @click.stop="openTermsModal" @keydown.enter.prevent.stop="openTermsModal" @keydown.space.prevent.stop="openTermsModal">términos y condiciones</span>.</span>
          </div>
          <small v-if="termsError" class="checkout-check__error">Debes aceptar los términos y condiciones para continuar.</small>
        </div>

        <TermsModal v-model="showTermsModal" />

        <div v-if="userSearched" class="checkout-step__section">
          <h2 class="text-justify checkout-step__section-title"><strong>Responsable de retiro</strong></h2>
          <small class="form-text text-muted checkout-step__section-text">Completa un solo perfil de retiro para mantener la entrega clara y ordenada.</small>

          <article class="checkout-guest-card" style="animation-delay: 0ms;">
            <div class="checkout-guest-card__header">
              <span class="checkout-guest-card__eyebrow">Entrega principal</span>
                <strong class="checkout-guest-card__title">Datos de retiro</strong>
                <span class="checkout-guest-card__meta">Un solo responsable para una entrega elegante</span>
            </div>

            <div class="checkout-form-grid checkout-form-grid--compact">
              <div class="checkout-field">
                <label class="checkout-label">Nombres</label>
                <input class="form-control checkout-input" v-model="ticketsTable[0].first_name" type="text" name="first_name" placeholder="Nombre de quien retira" required="">
              </div>

              <div class="checkout-field">
                <label class="checkout-label">Apellidos</label>
                <input class="form-control checkout-input" v-model="ticketsTable[0].last_name" type="text" name="last_name" placeholder="Apellido de quien retira" required="">
              </div>

              <template v-if="primaryTicketGuestFields.length">
                <div class="checkout-field checkout-field--full checkout-guest-extra-group">
                  <div class="checkout-guest-extra-group__copy">
                    <label class="checkout-label">Campos adicionales</label>
                    <small class="checkout-guest-extra-group__hint">Se copiaran al responsable principal de retiro.</small>
                  </div>
                </div>

                <div v-for="field in primaryTicketGuestFields" :key="field.slug" class="checkout-field checkout-field--full checkout-guest-extra">
                  <div class="checkout-guest-extra__header">
                    <label class="checkout-label">{{ field.label }}</label>
                    <span :class="['checkout-guest-extra__badge', field.is_required ? 'is-required' : 'is-optional']">{{ field.is_required ? 'Obligatorio' : 'Opcional' }}</span>
                  </div>

                  <small v-if="field.help_text" class="checkout-guest-extra__help">{{ field.help_text }}</small>

                  <input
                    v-if="field.field_type === 'field'"
                    class="form-control checkout-input"
                    :value="guestFieldValue(0, field.slug)"
                    type="text"
                    :placeholder="`Escribe ${String(field.label || '').toLowerCase()}`"
                    @input="setGuestFieldValue(0, field.slug, $event.target.value)"
                  >

                  <div v-else class="checkout-guest-extra__options">
                    <button
                      v-for="option in field.options || []"
                      :key="option"
                      :class="['checkout-guest-extra__option', { 'is-active': guestFieldValue(0, field.slug) === option }]"
                      type="button"
                      @click="setGuestFieldValue(0, field.slug, option)"
                    >
                      {{ option }}
                    </button>
                  </div>

                  <small v-if="fieldHasError(0, field.slug)" class="checkout-check__error">Completa este campo para continuar.</small>
                </div>
              </template>
            </div>
          </article>

          <p v-if="error" class="checkout-check__error checkout-step__field-error">{{ error }}</p>

          <div v-if="!loadingUser" class="checkout-step__actions checkout-step__actions--dock">
            <div class="checkout-step__actions-copy">
              <span class="checkout-step__summary-label">Paso 2 listo</span>
              <small class="checkout-step__summary-note">{{ ticketsTable.length }} acceso(s) listos para continuar</small>
            </div>
            <button class="btn btn-primary btn-block checkout-step__button liquid-page-button" type="submit">
              <span class="liquid-page-button__glow"></span>
              <span class="liquid-page-button__ring"></span>
              <span class="liquid-page-button__inner">
                <span class="liquid-page-button__copy">Continuar</span>
              </span>
            </button>
          </div>

          <div v-if="loadingUser" class="d-flex justify-content-center checkout-step__loader">
            <scaling-squares-spinner :size="60" color="#bec7d2" />
          </div>
        </div>
      </form>
    </div>
  </section>
</template>


  


<script>
import { apiService } from "../../common/api.services.js";
import { ScalingSquaresSpinner   } from 'epic-spinners';
import TermsModal from '../TermsModal.vue'


export default {
  
    name: 'GuestNameBar',
    props:{
        productsProp: {
          type: Object
       },
       order_finalF: {
       },

        event: {
           type: Object
       },

       selectedSeats:{
         type: Array
       },

       seatsProducts:{
         type: Array
       }

       
     },


     components: {
    ScalingSquaresSpinner,
    TermsModal,
  },

     

     data(){
      return{
        name: "",
        last_name: "",
        cc:"",
       events:[],
       next:null,
       ticketsTable:[],
       order:[],
       total: 0.00,
       fee: 0.00,
       email: "",
       phone: "",
        userSearched: false,
        password: "",
        loadingUser: false,
        loadingMain: false,
        term: "",
        address: "",
        require_account: false,
        customer: null,
        guestFieldErrors: {},
        timer: null,
        looking: false,
        success: false,
        error: "",
        acceptedTerms: false,
        termsError: false,
        showTermsModal: false,
        
      }
     

   },

    computed: {
      primaryTicketGuestFields() {
        const merged = {}
        this.ticketsTable.forEach((ticket) => {
          this.resolveGuestFields(ticket).forEach((field, index) => {
            const existing = merged[field.slug]
            if (!existing) {
              merged[field.slug] = {
                ...field,
                sort_order: Number(field.sort_order == null ? index : field.sort_order),
              }
              return
            }

            merged[field.slug] = {
              ...existing,
              is_required: Boolean(existing.is_required || field.is_required),
              options: Array.isArray(existing.options) && existing.options.length ? existing.options : field.options,
            }
          })
        })
        return Object.values(merged).sort((left, right) => {
          if (left.sort_order === right.sort_order) {
            return String(left.label || '').localeCompare(String(right.label || ''))
          }
          return left.sort_order - right.sort_order
        })
      },
    },

     created () {
      this.createTable()
  },

  
   methods:{


    


   

    createTable() {
    this.ticketsTable = []


///fetch('https://api.ipify.org/?format=json')
///.then(function(response) {
  //response.json().then(jsonData => {
    //console.log("Llega a ip");

    //console.log(jsonData);
  //});
//})
//.catch(function(error) {
  //console.log("Error ip");
  //7console.log(error)
//});




//fetch('https://ipapi.co/' + this.term + '/json/')
//.then(function(response) {
  //response.json().then(jsonData => {

    //console.log("Llega a  geo");

    //console.log(jsonData);

  //this.city=jsonData.city
  //this.region=jsonData.region


  //});
//})
//.catch(function(error) {
  //console.log("Error geo");
  //console.log(error)
  //this.city=""
 //// this.region=""
//});
    for (var i = 0; i < this.productsProp.length; i++){      
      for (let index = 0; index < this.productsProp[i].quantity; index++) {

        for (let j = 0; j < this.productsProp[i].quantity_factor; j++) {
          
        let uProduct =  {quantity:1, amount: this.productsProp[i].amount, product: this.productsProp[i].id , name: this.productsProp[i].name, is_courtesy:  this.productsProp[i].is_courtesy, first_name: "",  last_name: "", payment_term: 1, ig: "", age: "", cc: "", size: "", gender: 0, entry: "", seat: "", guest_fields: this.normalizeGuestFields(this.productsProp[i].guest_fields), extra_answers: {}};
        this.ticketsTable=this.ticketsTable.concat(uProduct)
       
      }
      }
      } 


      let numberF = 0

      for (i = 0; i < this.seatsProducts.length; i++){  

      for (let index = 0; index < this.seatsProducts[i].quantity; index++) {

        for (let j = 0; j < this.seatsProducts[i].quantity_factor; j++) {
         
            let uProduct =  {quantity:1, amount: this.seatsProducts[i].amount, product: this.seatsProducts[i].id , name: this.seatsProducts[i].name, is_courtesy:  this.seatsProducts[i].is_courtesy, first_name: "",  last_name: "", payment_term: 1, ig: "", age: "", cc: "", size: "", gender: 0, entry: "", seat: this.selectedSeats[numberF].id, seat_name: this.selectedSeats[numberF].name, seat_assigned: true, guest_fields: this.normalizeGuestFields(this.seatsProducts[i].guest_fields), extra_answers: {}};
             this.ticketsTable=this.ticketsTable.concat(uProduct)
             numberF++
       
      }
      }
      } 



      








      if (this.event.quick_checkout_enabled && !this.hasConfiguredGuestFields()){
        this.customer=1
        this.$emit('messageFromGuestName', this.ticketsTable, this.customer)
      }
    },

    normalizeGuestFields(fields) {
      if (!Array.isArray(fields)) {
        return []
      }

      return fields
        .filter((field) => field && field.is_active !== false)
        .map((field, index) => ({
          ...field,
          options: Array.isArray(field.options) ? field.options : [],
          sort_order: Number(field.sort_order == null ? index : field.sort_order),
        }))
        .sort((left, right) => {
          if (left.sort_order === right.sort_order) {
            return String(left.label || '').localeCompare(String(right.label || ''))
          }
          return left.sort_order - right.sort_order
        })
    },

    resolveGuestFields(ticket) {
      return Array.isArray(ticket && ticket.guest_fields) ? ticket.guest_fields : []
    },

    hasConfiguredGuestFields() {
      return this.ticketsTable.some((ticket) => this.resolveGuestFields(ticket).length > 0)
    },

    guestFieldErrorKey(ticketIndex, slug) {
      return `${ticketIndex}:${slug}`
    },

    guestFieldValue(ticketIndex, slug) {
      const answers = this.ticketsTable[ticketIndex] && this.ticketsTable[ticketIndex].extra_answers
      return answers && typeof answers[slug] === 'string' ? answers[slug] : ''
    },

    setGuestFieldValue(ticketIndex, slug, value) {
      const currentTicket = this.ticketsTable[ticketIndex] || {}
      const nextAnswers = {
        ...(currentTicket.extra_answers || {}),
        [slug]: value,
      }
      this.ticketsTable.splice(ticketIndex, 1, {
        ...currentTicket,
        extra_answers: nextAnswers,
      })
      const errors = { ...this.guestFieldErrors }
      delete errors[this.guestFieldErrorKey(ticketIndex, slug)]
      this.guestFieldErrors = errors
      this.error = ''
    },

    fieldHasError(ticketIndex, slug) {
      return Boolean(this.guestFieldErrors[this.guestFieldErrorKey(ticketIndex, slug)])
    },

    validateGuestFields() {
      const nextErrors = {}

      this.primaryTicketGuestFields.forEach((field) => {
        if (!field.is_required) {
          return
        }

        if (this.guestFieldValue(0, field.slug) !== '') {
          return
        }

        nextErrors[this.guestFieldErrorKey(0, field.slug)] = true
      })

      this.guestFieldErrors = nextErrors
      if (Object.keys(nextErrors).length) {
        this.error = 'Completa los campos adicionales obligatorios antes de continuar.'
        return false
      }

      this.error = ''
      return true
    },


      getCustomer(){



        this.userSearched=false
      let endpoint = `/api/contacts/?email=${this.email}`; //open pos


      console.log(endpoint)

        clearTimeout(this.timer)

        this.timer = setTimeout(() => {
                
      apiService(endpoint).then(data => {
        if (data.length==0){
          this.require_account=true
          this.userSearched=true

        
        }else{
          this.customer = data[0];
          this.require_account=false
          this.userSearched=true

        }
        this.looking=false

      });
    
    }, 1250)
  },



  continueSale(){

     this.termsError = false
     this.error = ''

     if (this.userSearched){
       this.loadingUser=true

      if (!this.validateGuestFields()) {
        this.loadingUser=false
        return
      }


       let m_name=this.ticketsTable[0].first_name
       let l_name=this.ticketsTable[0].last_name
       let extraAnswers = { ...(this.ticketsTable[0].extra_answers || {}) }


       for (let j = 0; j < this.ticketsTable.length; j++) {
         this.ticketsTable[j].first_name=m_name
         this.ticketsTable[j].last_name=l_name 
         this.ticketsTable[j].extra_answers = { ...extraAnswers }
       }

      
      if(this.require_account==false){
  
      this.$emit('messageFromGuestName', this.ticketsTable, this.customer)
      }else{
        if (!this.acceptedTerms) {
          this.loadingUser=false
          this.termsError = true
          return
        }
        this.createCustomer()
      }
  }
    },

  toggleAcceptedTerms(){
    this.acceptedTerms = !this.acceptedTerms
    if (this.acceptedTerms) {
      this.termsError = false
    }
  },

  openTermsModal(){
    this.showTermsModal = true
  },


 createCustomer(){
    let first_name=this.ticketsTable[0].first_name
    let last_name=this.ticketsTable[0].last_name
    this.loadingMain=true
    this.success = false
    var profile = new Object();
    profile.phone =  this.phone
    profile.address = this.address
    profile.city = ""
    if (this.password==""){
      this.password="fomoplankk9876"

    }
    if(this.email!=""){
        let endpoint =  `/api/user/create`
       
    apiService(endpoint, "POST", {
  
    password: this.password,
    contact_profile: profile,
    first_name: first_name,
    last_name: last_name,
    email: this.email,
    username: this.email

  })
              .then(data => {
                  if (data.id){
                    this.customer=data
                    console.log(data)
                    this.require_account=false

                   this.continueSale()
        
                  }else{

                      alert("Error al crear la cuenta. Revisa los datos e inténtalo nuevamente.")
                      this.error="Verifique que los datos sean correctos"
                      this.loadingUser=false

                  }

        
               
              })

              

    

      }else {
        this.error="Llene todos los campos"
      }
    },


    


    openEvent(_id) {
      this.$router.push({ name: "event", params: { id: _id } });
    },


    increment (quantity, i) {
      this.availableTickets[i].quantity++
      this.availableTickets[i].total= (this.availableTickets[i].quantity * this.availableTickets[i].amount).toFixed(2)
       this.availableTickets[i].amount=this.availableTickets[i].amount*1
      this.total=this.total + this.availableTickets[i].amount
      this.total.toFixed(2)
    },
    decrement (quantity, i) {
      if(quantity === 0) {
         this.availableTickets[i].quantity=0
           }else{

     
        this.availableTickets[i].quantity--
      this.availableTickets[i].total= (this.availableTickets[i].quantity * this.availableTickets[i].amount).toFixed(2)
       this.availableTickets[i].amount=this.availableTickets[i].amount*1
      this.total=this.total - this.availableTickets[i].amount
      this.total.toFixed(2)
       }
    
      
    }

    
   }
}
</script>

<style>

#imagemPosterSlide{
  padding: 10px;
  
}

.container{
  justify-content:center
}


.checkout-guest-extra-group__copy,
.checkout-guest-extra {
  display: grid;
  gap: 10px;
}

.checkout-guest-extra {
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(181, 201, 255, 0.18);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.02));
}

.checkout-guest-extra-group__hint,
.checkout-guest-extra__help,
.checkout-step__field-error {
  color: rgba(214, 224, 248, 0.74);
}

.checkout-guest-extra__header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.checkout-guest-extra__badge {
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.checkout-guest-extra__badge.is-required {
  color: #eef3ff;
  border: 1px solid rgba(196, 206, 228, 0.18);
  background: linear-gradient(180deg, rgba(216, 224, 239, 0.18), rgba(92, 102, 120, 0.28));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.checkout-guest-extra__badge.is-optional {
  color: #a6b6d9;
  background: rgba(166, 182, 217, 0.12);
}

.checkout-guest-extra__options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.checkout-guest-extra__option {
  border: 1px solid rgba(181, 201, 255, 0.18);
  background: rgba(255, 255, 255, 0.04);
  color: #eff4ff;
  border-radius: 999px;
  padding: 11px 14px;
  font-weight: 600;
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease;
}

.checkout-guest-extra__option:hover {
  transform: translateY(-1px);
  border-color: rgba(140, 243, 232, 0.45);
}

.checkout-guest-extra__option.is-active {
  color: #0e2f33;
  border-color: rgba(140, 243, 232, 0.8);
  background: linear-gradient(135deg, #8cf3e8, #d3ffef);
}

  
</style>



<style lang="css">
h1 {
  text-align: center;
  margin-bottom: 50px;
  margin-top: 50px;
}

.blog-card-blog {
  margin-top: 30px;
}

.blog-card {
  display: inline-block;
  position: relative;
  width: 100%;
  margin-bottom: 30px;
  border-radius: 6px;
  color: rgba(0, 0, 0, 0.87);
  background: rgb(52, 58, 64);
  background-color: rgb(52, 58, 64);
}



.blog-card .blog-table {
  padding: 15px 30px;
}

.blog-table {
  margin-bottom: 0px;
}

.blog-category {
  position: relative;
  line-height: 0;
  margin: 15px 0;
}

.blog-text-success {
  color: rgb(198,197,197);
}

.blog-card-blog .blog-card-caption {
  margin-top: 5px;
}

.blog-card-caption {
  font-weight: 700;
  font-family: "Roboto Slab", "Times New Roman", serif;
}

.blog-card-caption, .blog-card-caption a {
  color: #333;
  text-decoration: none;
}

p {
  color: #3C4857;
  margin-top: 0;
  margin-bottom: 1rem;
}

.blog-card .ftr {
  margin-top: 15px;
}

.blog-card .ftr .author {
  color: #888;
}

.blog-card .ftr div {
  display: inline-block;
}

.blog-card .author .avatar {
  width: 36px;
  height: 36px;
  overflow: hidden;
  border-radius: 50%;
  margin-right: 5px;
}

.blog-card .ftr .stats {
  position: relative;
  top: 1px;
  font-size: 14px;
}

.blog-card .ftr .stats {
  float: right;
  line-height: 30px;
}

a {
  text-decoration: none;
}


 .d-inline .input {
    border: 0;
    width: 2.5rem;
    text-align: center;
    padding: 0 .5rem;
  }

.quantity-toggle .button {
    background: rgb(198,197,197);
    font-size: 1rem;
    cursor: pointer;
    color: white;
  }


.cont{
   padding-right: 50px;
   padding-left: 50px;
}
.rw{
   padding-right: 5px;
   padding-left: 5px;
  border-top: 1px solid white;



}

@media (max-width: 768px) {
.cont {
    padding-left: 10px;
    padding-right: 10px;
}

.rw{
   padding-right: 10px;
   padding-left: 10px;


}
}


    
</style>

   
